import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, Check, Heart, Sparkles, Calendar as CalendarIcon, MapPin, Users, MessageCircle } from "lucide-react";

const STEP_META = [
  { key: "type", title: "What are you planning?", Icon: Heart },
  { key: "when", title: "When & how many nights?", Icon: CalendarIcon },
  { key: "where", title: "Where & how many guests?", Icon: MapPin },
  { key: "you", title: "Style & your details", Icon: Users },
];

const TYPES = [
  { value: "wedding", title: "Destination Wedding", desc: "A full traditional Indian wedding in a heritage venue" },
  { value: "mock-wedding", title: "Mock Wedding (1 day)", desc: "A symbolic ceremonial experience — non-legally-binding" },
  { value: "honeymoon", title: "Honeymoon", desc: "Romantic itinerary for couples — Taj, Kashmir, Kerala & more" },
];

const STYLES = ["Traditional Hindu", "Palace / Heritage", "Beach", "Desert / Camp", "Himalayan / Mountains", "Backwater / Kerala", "Modern minimalist", "Not sure — surprise us"];
const BUDGETS = ["Under $3,000", "$3,000 – $7,000", "$7,000 – $15,000", "$15,000 – $30,000", "$30,000+"];

export default function WeddingInquiryDialog({ open, onOpenChange, defaultType = "wedding" }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    inquiry_type: defaultType,
    event_date: "",
    nights: "",
    city: "",
    guest_count: "",
    style: "",
    budget_range: "",
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm((f) => ({ ...f, inquiry_type: defaultType }));
    }
  }, [open, defaultType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canAdvance = () => {
    if (step === 0) return !!form.inquiry_type;
    if (step === 1) return !!form.event_date;
    if (step === 2) return true; // city + guests optional
    if (step === 3) return !!form.name && !!form.email && !!form.phone;
    return false;
  };

  const submit = async () => {
    if (!canAdvance()) {
      toast.error("Please fill required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        country: form.country || null,
        message: form.message || null,
        travel_date: form.event_date || null,
        travelers: form.guest_count ? parseInt(form.guest_count) : 2,
        inquiry_type: form.inquiry_type,
        event_date: form.event_date || null,
        city: form.city || null,
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        nights: form.nights ? parseInt(form.nights) : null,
        style: form.style || null,
        budget_range: form.budget_range || null,
        tour_interest: form.inquiry_type,
      };
      await api.post("/inquiries", payload);
      toast.success("Thank you! Our curator will be in touch within 24 hours.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const StepIcon = STEP_META[step].Icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="wedding-inquiry-dialog">
        <DialogHeader className="bg-deepred text-white px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-white/12 border border-white/25 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-marigold" />
            </div>
            <div>
              <DialogTitle className="text-white text-xl font-serif">Plan your celebration</DialogTitle>
              <DialogDescription className="text-white/75 text-xs uppercase tracking-[0.2em] mt-0.5">
                Step {step + 1} of {STEP_META.length} · {STEP_META[step].title}
              </DialogDescription>
            </div>
          </div>
          {/* Progress bar */}
          <div className="flex gap-1.5 mt-5">
            {STEP_META.map((_, i) => (
              <div
                key={i}
                className={`h-[3px] flex-1 rounded-full transition-colors ${i <= step ? "bg-marigold" : "bg-white/20"}`}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="px-8 py-7 max-h-[70vh] overflow-y-auto" data-testid={`wedding-step-${step}`}>
          {step === 0 && (
            <div className="space-y-3">
              {TYPES.map((t) => (
                <label
                  key={t.value}
                  data-testid={`type-${t.value}`}
                  className={`flex items-start gap-4 p-5 border rounded-sm cursor-pointer transition-all ${
                    form.inquiry_type === t.value ? "border-saffron bg-saffron/5" : "border-cream-200 hover:border-saffron/60"
                  }`}
                >
                  <input
                    type="radio"
                    name="inquiry_type"
                    value={t.value}
                    checked={form.inquiry_type === t.value}
                    onChange={(e) => set("inquiry_type", e.target.value)}
                    className="mt-1 accent-saffron"
                  />
                  <div>
                    <div className="font-medium text-foreground">{t.title}</div>
                    <div className="text-sm text-foreground/60 mt-1">{t.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="event_date">Preferred date <span className="text-deepred">*</span></Label>
                <Input
                  id="event_date"
                  type="date"
                  value={form.event_date}
                  onChange={(e) => set("event_date", e.target.value)}
                  data-testid="input-event-date"
                  className="mt-1.5"
                />
                <p className="text-xs text-foreground/50 mt-1">If flexible, pick any date in the month you have in mind.</p>
              </div>
              {form.inquiry_type === "honeymoon" && (
                <div>
                  <Label htmlFor="nights">Number of nights</Label>
                  <Input
                    id="nights"
                    type="number"
                    min="3"
                    max="30"
                    value={form.nights}
                    onChange={(e) => set("nights", e.target.value)}
                    data-testid="input-nights"
                    placeholder="e.g. 7, 10 or 14"
                    className="mt-1.5"
                  />
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="city">Preferred city / venue</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="e.g. Udaipur, Jaipur, Agra, Kerala, Kashmir"
                  className="mt-1.5"
                  data-testid="input-city"
                />
              </div>
              {(form.inquiry_type === "wedding" || form.inquiry_type === "mock-wedding") && (
                <div>
                  <Label htmlFor="guest_count">Expected guest count</Label>
                  <Input
                    id="guest_count"
                    type="number"
                    min="2"
                    value={form.guest_count}
                    onChange={(e) => set("guest_count", e.target.value)}
                    placeholder={form.inquiry_type === "mock-wedding" ? "e.g. 2, 5, 20 (up to 20 included)" : "e.g. 80, 150, 300"}
                    className="mt-1.5"
                    data-testid="input-guest-count"
                  />
                </div>
              )}
              <div>
                <Label>Style you envision</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`style-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => set("style", s)}
                      className={`px-3.5 py-1.5 border text-sm rounded-sm transition-colors ${
                        form.style === s ? "border-saffron bg-saffron text-white" : "border-cream-200 hover:border-saffron"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full name <span className="text-deepred">*</span></Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    data-testid="input-name"
                    className="mt-1.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={(e) => set("country", e.target.value)}
                    data-testid="input-country"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email <span className="text-deepred">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="input-email"
                    className="mt-1.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone <span className="text-deepred">*</span></Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    data-testid="input-phone"
                    className="mt-1.5"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Budget (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BUDGETS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      data-testid={`budget-${b.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}
                      onClick={() => set("budget_range", b)}
                      className={`px-3.5 py-1.5 border text-sm rounded-sm transition-colors ${
                        form.budget_range === b ? "border-saffron bg-saffron text-white" : "border-cream-200 hover:border-saffron"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="message">Anything special we should know?</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="input-message"
                  rows={3}
                  placeholder="Dietary needs, surprise ideas, family arrangements, must-visit places..."
                  className="mt-1.5"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-8 py-5 border-t border-cream-200 bg-cream/40">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => (step === 0 ? onOpenChange(false) : setStep((s) => s - 1))}
              data-testid="wedding-back"
              className="text-foreground/70"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {step === 0 ? "Close" : "Back"}
            </Button>
            <a
              href="https://wa.me/917017798524?text=Hi%20TTI%20Holidays%2C%20I%27d%20like%20to%20plan%20my%20special%20celebration"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wedding-whatsapp"
              className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 bg-[#25D366] text-white text-xs font-medium rounded-sm hover:bg-[#128C7E] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Chat on WhatsApp
            </a>
          </div>
          {step < STEP_META.length - 1 ? (
            <Button
              onClick={() => (canAdvance() ? setStep((s) => s + 1) : toast.error("Please choose an option to continue"))}
              disabled={!canAdvance()}
              data-testid="wedding-next"
              className="bg-deepred hover:bg-saffron text-white"
            >
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting || !canAdvance()}
              data-testid="wedding-submit"
              className="bg-saffron hover:bg-marigold text-white"
            >
              {submitting ? "Submitting…" : (<><Check className="w-4 h-4 mr-1.5" /> Send request</>)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
